<template>
  <div class="registry-common-landing-container">
    <div class="grid-x grid-margin-x medium-margin-top-s small-margin-top-xs registry-bg-container-grid">
      <div class="cell small-12 medium-6 large-6 xlarge-6 registry-bg-container wedding-bg-container">
        <RegistryUiLandingHero
          class="registry-bg-image"
          variant="tile"
          :desktop-src="weddingDesktopImage"
          :mobile-src="weddingMobileImage"
          :width="tileWidth"
          :height="tileHeight"
          :desktop-placeholder="WeddingLandingPage.placeholderNewWeddingDesktop"
          :mobile-placeholder="WeddingLandingPage.placeholderNewWeddingMobile"
          placeholder-variant="skeleton"
          alt="Wedding Registry Landing Background Image"
        />
        <a href="/registry/wedding" class="registry-content" aria-label="Wedding Registry Get Started" @click.prevent="onGetStarted('wedding')">
          <div class="h3">Wedding<br />Registry</div>
          <span class="link-body registry-landing-link">{{ WeddingLandingPage.getStartedCTA }}</span>
        </a>
      </div>
      <div class="cell small-12 medium-6 large-6 xlarge-6 registry-bg-container baby-bg-container">
        <RegistryUiLandingHero
          class="registry-bg-image"
          variant="tile"
          :desktop-src="babyDesktopImage"
          :mobile-src="babyMobileImage"
          :width="tileWidth"
          :height="tileHeight"
          :desktop-placeholder="BabyLandingPage.placeholderNewBabyDesktop"
          :mobile-placeholder="BabyLandingPage.placeholderNewBabyMobile"
          placeholder-variant="skeleton"
          fetch-priority="auto"
          alt="Baby Registry Landing Background Image"
        />
        <a href="/registry/baby" class="registry-content" aria-label="Baby Registry Get Started" @click.prevent="onGetStarted('baby')">
          <div class="h3">Baby<br />Registry</div>
          <span class="link-body registry-landing-link">{{ BabyLandingPage.getStartedCTA }}</span>
        </a>
      </div>
    </div>

    <div class="find-registry-bg-container medium-margin-top-s small-margin-top-xs">
      <SearchRegistryBarForm ref="searchRegistryBarForm" :is-registry-landing="true" />
    </div>

    <div v-if="!isSignedIn" class="grid-x align-center sign-in-container">
      <p :class="{ medium: isBcom }">Already have a registry?</p>
      <AtomicUiButton variant="link" :class="`margin-left-xxs ${isBcom ? 'link-med' : 'link-body'}`" @click="onSignIn">Sign in</AtomicUiButton>
    </div>
  </div>
  <LazyCanvasAemCanvasMain :canvas="canvas" hydrate-on-visible />
</template>

<script setup>
import { fireTag } from '@nuxt-common/util/app/utils/tagManager';
import { Cookie } from '@nuxt-common/util/app/utils/cookie';
import { breakpoints } from '~/config/breakpoints';
import SearchRegistryBarForm from '~/components/SearchRegistry/SearchRegistryBarForm';
import { WeddingLandingPage, BabyLandingPage } from 'config';
import { UPDATE_DEFAULT_REGISTRY } from '~/types/actions';
import { SET_REGISTRY_ID_STATUS, SET_REGISTRY_STATUS_OCCASION } from '~/types/mutations';
import { useEnvPropsStore } from '~/stores/envProps';
import { useBaseStore } from '~/stores/base';
import { useCreateRegistryStore } from '~/stores/createRegistry';
import { useAssociation } from '~/composables/useAssociation';

provide('isRegistry', true);
const emits = defineEmits(['toggleDeleteModalOverlay']);

const { getDefaultRegistry, isManageRegistryExceededForLanding, getUserRegistries } = useAssociation();

const envPropsStore = useEnvPropsStore();
const baseStore = useBaseStore();
const createRegistryStore = useCreateRegistryStore();

const searchRegistryBarForm = ref(null);

const signedInCookie = useCookie('SignedIn');
const isSignedIn = computed(() => !!Number(signedInCookie.value));
const orgId = computed(() => (envPropsStore.envProps.entryPoint?.toUpperCase() === 'MCOM' ? 12 : 13));
const isBcom = computed(() => orgId.value === 13);

const cdnUrl = computed(() => envPropsStore.envProps?.cdnUrl || '');
const weddingDesktopImage = computed(() => `${cdnUrl.value}/img/${WeddingLandingPage.imagePathNewWeddingDesktop}`);
const weddingMobileImage = computed(() => `${cdnUrl.value}/img/${WeddingLandingPage.imagePathNewWeddingMobile}`);
const babyDesktopImage = computed(() => `${cdnUrl.value}/img/${BabyLandingPage.imagePathNewBabyDesktop}`);
const babyMobileImage = computed(() => `${cdnUrl.value}/img/${BabyLandingPage.imagePathNewBabyMobile}`);

const toAvif = (url) => (/\.webp(\?|$)/i.test(url || '') ? url.replace(/\.webp(\?|$)/i, '.avif$1') : null);

const weddingDesktopImageAvif = computed(() => toAvif(weddingDesktopImage.value));
const babyDesktopImageAvif = computed(() => toAvif(babyDesktopImage.value));
const weddingMobileImageAvif = computed(() => toAvif(weddingMobileImage.value));
const babyMobileImageAvif = computed(() => toAvif(babyMobileImage.value));

const tileWidth = computed(() => (isBcom.value ? 1024 : 740));
const tileHeight = computed(() => (isBcom.value ? 819 : 593));

useHead(() => ({
  link: [
    {
      rel: 'preload',
      as: 'image',
      type: 'image/avif',
      href: weddingDesktopImageAvif.value,
      media: `(min-width: ${breakpoints.medium}px)`,
      fetchpriority: 'high',
    },
    {
      rel: 'preload',
      as: 'image',
      type: 'image/avif',
      href: weddingMobileImageAvif.value,
      media: `(max-width: ${breakpoints.medium - 1}px)`,
      fetchpriority: 'high',
    },
    {
      rel: 'preload',
      as: 'image',
      type: 'image/avif',
      href: babyDesktopImageAvif.value,
      media: `(min-width: ${breakpoints.medium}px)`,
      fetchpriority: 'auto',
    },
    {
      rel: 'preload',
      as: 'image',
      type: 'image/avif',
      href: babyMobileImageAvif.value,
      media: `(max-width: ${breakpoints.medium - 1}px)`,
      fetchpriority: 'auto',
    },
  ],
}));

const deviceType = computed(() => (envPropsStore.envProps?.isMew ? 'PHONE' : 'DESKTOP'));

const { data: canvas } = useLazyAsyncData(
  `registry-landing-canvas-common`,
  () =>
    $fetch('/xapi/homepage/v1/canvas', {
      query: {
        page: 'commonRegistryLanding',
        _shoppingMode: 'WEDDING_REGISTRY',
        _deviceType: deviceType.value,
      },
    }),
  { server: false, default: () => ({}) },
);

useLazyAsyncData(
  'user-registries',
  () => {
    if (isSignedIn.value) {
      return getUserRegistries(true);
    }
    return {};
  },
  { server: false, default: () => ({}) },
);

const onSignIn = () => {
  Cookie.set('FORWARDPAGE_KEY', '/registry');
  window.location.assign('/account/signin');
};

const onGetStarted = async (occasion) => {
  const registry = getDefaultRegistry(occasion);
  if (isSignedIn.value && registry.registryGUID && isManageRegistryExceededForLanding(occasion)) {
    await baseStore[UPDATE_DEFAULT_REGISTRY]({
      orgId: orgId.value,
      registryGUID: registry.registryGUID,
    });
    navigateTo('/registry/manage');
    createRegistryStore[SET_REGISTRY_STATUS_OCCASION](occasion);
    createRegistryStore[SET_REGISTRY_ID_STATUS](true);
    emits('toggleDeleteModalOverlay');
  } else {
    navigateTo(`/registry/${occasion}`);
  }
};

onMounted(() => {
  searchRegistryBarForm.value.clearSearchInput();

  if (window.sessionStorage.getItem('registryFetch')) {
    window.sessionStorage.removeItem('registryFetch');
  }

  const fireAnalyticTag = () => {
    fireTag('view', {
      page_name: 'registry landing',
      page_type: 'registry',
    });
  };

  const fireAudiencePixel = () => {
    const randomId = crypto.randomUUID();
    new Image().src = `https://pubads.g.doubleclick.net/activity;dc_iu=/22026345640/DFPAudiencePixel;ord=${randomId};dc_seg=6952796602?`;
  };

  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(fireAnalyticTag);
    requestIdleCallback(fireAudiencePixel);
  } else {
    setTimeout(fireAnalyticTag, 1000);
    setTimeout(fireAudiencePixel, 2000);
  }
});
</script>

<style lang="scss" scoped>
@import '~@core/foundation/scss/foundation-6.7.4/scss/prototype/_spacing.scss';

.registry-common-landing-container {
  margin-bottom: rem-calc(38);
  @include breakpoint(small only) {
    margin-bottom: rem-calc(16);
    margin-right: rem-calc(-20);
    margin-left: rem-calc(-20);

    .registry-bg-container-grid {
      @include margin-direction('left', map-get($spacer, 'z'));
      @include margin-direction('right', map-get($spacer, 'z'));
    }
    .registry-bg-container {
      @include margin-direction('left', map-get($spacer, 'z'));
      @include margin-direction('right', map-get($spacer, 'z'));
      width: 100%;
    }
  }
  .registry-bg-container {
    position: relative;
    text-align: center;
  }
  .registry-content {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: white;
    padding: rem-calc(40 25 54 25);
    width: rem-calc(201);
    display: grid;
    cursor: pointer;
    @include breakpoint(small only) {
      padding: rem-calc(49 24 47 24);
    }

    &,
    &:hover,
    &:focus {
      text-decoration: none;
    }
  }
  .registry-landing-link {
    margin-top: rem-calc(12);
    @include breakpoint(small only) {
      margin-top: rem-calc(16);
    }
  }
  .find-registry-bg-container {
    background-color: $background-color;
    padding-top: rem-calc(28);
    padding-bottom: rem-calc(28);
    @include breakpoint(small only) {
      padding-top: rem-calc(38);
      padding-bottom: rem-calc(33);
    }
  }
  .baby-bg-container {
    @include breakpoint(small only) {
      margin-top: rem-calc(16);
    }
  }
  .sign-in-container {
    margin-top: rem-calc(16);
  }
  .registry-bg-image {
    width: 100%;
    width: -moz-available;
    width: -webkit-fill-available;
    width: fill-available;
    object-fit: cover;
  }
}
</style>

<style lang="scss" brand="mcom" scoped>
@import '~/assets/scss/views/registryLandingPageViewStyles.scss';
@include custom-font-setup('tt-ramillas');
</style>

<style lang="scss" brand="bcom" scoped>
@import '~/assets/scss/views/registryLandingPageViewStyles.scss';
.registry-common-landing-container {
  @include breakpoint(medium up) {
    margin-bottom: rem-calc(40);
    .sign-in-container {
      margin-top: rem-calc(24);
    }
    .registry-content {
      padding: rem-calc(59 25 59 25);
    }
  }
  @include breakpoint(small only) {
    .find-registry-bg-container {
      padding-top: rem-calc(32);
    }
  }
  .registry-content {
    opacity: 0.85;
  }
}
</style>
